import "./ArtistCard.scss";

import { useTranslation } from "react-i18next";

import { ARTIST_FALLBACK_IMAGE } from "@/config/artistFallback";
import { buildPagePath } from "@/config/pages";
import type { ArtistProfile } from "@/content/artists";

import useAnimatedNavigate from "../../../hooks/useAnimatedNavigate";

interface ArtistCardProps {
  artist: ArtistProfile;
}

const ArtistCard = ({ artist }: ArtistCardProps) => {
  const { t, i18n } = useTranslation();
  const animatedNavigate = useAnimatedNavigate();
  const lang = i18n.resolvedLanguage ?? "fr";

  const photoSrc = artist.images.portrait || ARTIST_FALLBACK_IMAGE;
  const isFallbackPhoto = !artist.images.portrait;

  const shortBio = artist.bio.short
    ? lang === "en"
      ? artist.bio.short.en
      : artist.bio.short.fr
    : undefined;

  const handleClick = () => {
    animatedNavigate(buildPagePath("artistDetail", { slug: artist.slug }));
  };

  return (
    <div
      className="artistCard"
      onClick={handleClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="imageWrap">
        <img
          src={photoSrc}
          alt={artist.name}
          className={`image${isFallbackPhoto ? " imageFallback" : ""}`}
          loading="lazy"
          decoding="async"
        />
        <div className="overlay">
          <span className="discover">{t("artists.discover")} →</span>
        </div>
      </div>
      <div className="info">
        <h3 className="name">{artist.name}</h3>
        <span className="style">{artist.styles.join(" / ")}</span>
        {shortBio && <p className="bio">{shortBio}</p>}
      </div>
    </div>
  );
};

export default ArtistCard;
